import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import { SCIENCE_STATS, PRINCIPLES } from "../data/content";

const Science = () => (
  <div>
    <PageHero
      overline="The Science of Daily Wellness"
      title="Rooted in science. Made for life."
      subtitle="At NARA Nutrition, every formula begins with purpose and is built on clinically informed research. Our daily wellness supplement delivers ingredient density, bioavailability and transparency — so you feel the difference, every day."
      image="/images/lifestyle_woman_science.png"
      imageAlt="Woman, science of wellness"
    />

    {/* STATS */}
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {SCIENCE_STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.07}>
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(15,23,42,0.04)] h-full">
              <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-sky-50 text-royal mb-5">
                <Icon name={s.icon} className="w-6 h-6" />
              </span>
              <p className="font-heading text-4xl font-bold text-navy-900">{s.value}</p>
              <p className="font-semibold text-navy-900 mt-1">{s.label}</p>
              <p className="text-sm text-slate-400 mt-2 leading-snug">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* PRINCIPLES */}
    <section className="bg-[#FAFBFD] border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <Reveal className="max-w-2xl mb-12">
          <span className="text-xs font-bold tracking-[0.22em] uppercase text-royal mb-4 block">Our Ingredient Principles</span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-navy-900">Purposeful ingredients. Proven impact.</h2>
          <p className="mt-5 text-slate-500 leading-relaxed">
            We source the highest quality, science-backed ingredients that work in synergy to support your body's natural intelligence — day after day.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07}>
              <div className="flex gap-5 bg-white rounded-2xl p-7 border border-slate-100 hover:shadow-xl transition-all duration-300 h-full">
                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-royal text-white shrink-0">
                  <Icon name={p.icon} className="w-6 h-6" />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-navy-900">{p.title}</h3>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Link to="/ingredients" className="mt-10 inline-flex items-center gap-2 text-royal font-semibold hover:gap-3 transition-all">
          Learn more about our ingredients <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>

    {/* FEATURE IMAGE */}
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
      <Reveal>
        <div className="relative rounded-[2rem] overflow-hidden shadow-xl">
          <img
            src="/images/gen/science_feature.png"
            alt="The science of daily wellness"
            className="w-full h-[360px] md:h-[520px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-navy-900/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-xl">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-sky-300 mb-3 block">Science You Can Feel</span>
            <h2 className="font-heading text-2xl md:text-4xl font-semibold text-white leading-tight">
              Wellness, designed with intention.
            </h2>
            <p className="mt-3 text-slate-200 text-sm md:text-base leading-relaxed">
              Every formula is built on clinical evidence and refined for real life — so feeling your best becomes a daily habit.
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  </div>
);

export default Science;
